import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiFileText } from 'react-icons/fi';
import { 
  FiFile, 
  FiFileText as FiFileTextIcon, 
  FiClock, 
  FiArrowLeft as FiArrowLeftIcon, 
  FiArrowRight, 
  FiRepeat,
  FiHash,
  FiFolder,
  FiDollarSign
} from 'react-icons/fi';
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import './TransactionDetails.css';
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useLanguage } from "../../contexts/LanguageContext";

const DEFAULT_AVATAR =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMiIgeT0iMTIiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Rya2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgo8cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz4KPC9zdmc+Cjwvc3ZnPg==";

function resolveAvatarUrl(userId, avatarFilename) {
  if (!avatarFilename) return DEFAULT_AVATAR;
  if (typeof avatarFilename === "string" && avatarFilename.startsWith("data:")) return avatarFilename;
  if (typeof avatarFilename === "string" && /^https?:\/\//i.test(avatarFilename)) return avatarFilename;
  try {
    const { data } = supabase.storage.from("avatars").getPublicUrl(`${userId}/${avatarFilename}`);
    return data?.publicUrl || DEFAULT_AVATAR;
  } catch {
    return DEFAULT_AVATAR;
  }
}

const fallbackAvatar = DEFAULT_AVATAR;

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  return `${month} ${day}, ${year} • ${hours}:${minutes} ${ampm}`;
};

const formatAmount = (amount) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  return Number.isFinite(num) ? num.toFixed(3) : '0.000';
};
const formatUSD = (winiAmount, winiToUsdRate) => {
  if (!winiToUsdRate) return '0.00';
  const num = typeof winiAmount === 'string' ? parseFloat(winiAmount) : Number(winiAmount);
  const usdAmount = num * winiToUsdRate;
  return Number.isFinite(usdAmount) ? usdAmount.toFixed(2) : '0.00';
};

export default function TransactionDetails() {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [winiToUsdRate, setWiniToUsdRate] = useState(null);
  const { translations: t } = useLanguage();

  // user profile (sender or receiver depending on flow)
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState(fallbackAvatar);
  const [userUsername, setUserUsername] = useState('');

  // printable area ref
  const printRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.id || !transactionId) {
      setLoading(false);
      return;
    }

    const fetchTransactionDetails = async () => {
      try {
        setLoading(true);

        // Fetch winiToUsdRate from app_settings
        const { data: appSettings, error: settingsError } = await supabase
          .from('app_settings')
          .select('wini_to_usd_rate')
          .limit(1)
          .single();

        if (!settingsError && appSettings) {
          setWiniToUsdRate(appSettings.wini_to_usd_rate || null);
        }

        // Fetch transaction from Supabase
        const { data: txData, error: txError } = await supabase
          .from('transactions')
          .select('*')
          .eq('id', transactionId)
          .eq('user_id', currentUser.id)
          .single();

        if (txError || !txData) {
          setTransaction(null);
          setLoading(false);
          return;
        }

        // Map Supabase fields to expected format
        const isIncoming = txData.flow === 'in';
        const otherUserId = isIncoming ? txData.sender_id : txData.receiver_id;

        const mappedTransaction = {
          id: txData.id,
          transaction_id: txData.transaction_id,
          source: txData.source || 'wallet',
          flow: txData.flow || 'in',
          field: txData.field || 'winicoins',
          amount: txData.amount || 0,
          oldBalance: txData.old_balance || 0,
          newBalance: txData.new_balance || 0,
          description: txData.description || '',
          fee_percentage: txData.fee_percentage || null,
          fee_amount_in_coins: txData.fee_amount_in_coins || null,
          // Calculate winicoinsAdded and winicoinsRemoved based on flow
          winicoinsAdded: txData.flow === 'in' ? Math.abs(txData.amount || 0) : 0,
          winicoinsRemoved: txData.flow === 'out' ? Math.abs(txData.amount || 0) : 0,
          time: txData.created_at || txData.updated_at,
          sender_id: txData.sender_id,
          receiver_id: txData.receiver_id,
        };

        setTransaction(mappedTransaction);

        // Fetch user profile (sender for incoming, receiver for outgoing)
        if (otherUserId && typeof otherUserId === 'string') {
          try {
            const { data: userProfile, error: profileError } = await supabase
              .from('profiles')
              .select('display_name, username, avatar_url')
              .eq('user_id', otherUserId)
              .single();

            if (!profileError && userProfile) {
              const name = userProfile.display_name || userProfile.username || 'User';
              setUserName(otherUserId === currentUser.id ? 'You' : name);
              setUserUsername(userProfile.username || userProfile.display_name || 'user');
              
              // Resolve avatar URL
              if (userProfile.avatar_url) {
                setUserAvatar(resolveAvatarUrl(otherUserId, userProfile.avatar_url));
              } else {
                setUserAvatar(fallbackAvatar);
              }
            } else {
              setUserName(String(otherUserId));
              setUserUsername('');
              setUserAvatar(fallbackAvatar);
            }
          } catch {
            setUserName(String(otherUserId));
            setUserUsername('');
            setUserAvatar(fallbackAvatar);
          }
        } else {
          // Use source as name if no user_id
          setUserName(txData.source || '—');
          setUserUsername('');
          setUserAvatar(fallbackAvatar);
        }
      } catch (e) {
        console.error('Error fetching transaction details:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionDetails();
  }, [transactionId, currentUser?.id]);

  const handleExportPDF = () => {
    // Simple, dependency-free export via the browser print dialog.
    // The print CSS below ensures only the card content is printed nicely.
    window.print();
  };

  if (loading) {
    return (
      <div className="transaction-details-page">
        <div className="transaction-details-loading">
          {t.transactionDetails?.loading || "Loading..."}
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="transaction-details-page">
        <div className="transaction-details-error">
        <p>{t.transactionDetails?.notFound || "Transaction not found"}</p>
        </div>
      </div>
    );
  }

  const isIncoming = transaction.flow === 'in';
  const transactionAmount = formatAmount(Math.abs(transaction.amount || 0));
  const oldBalance = formatAmount(transaction.oldBalance || 0);
  const newBalance = formatAmount(transaction.newBalance || 0);
  const feeAmount = formatAmount(transaction.fee_amount_in_coins || 0);
  const feePercentage = transaction.fee_percentage ? `${transaction.fee_percentage}%` : '';

  return (
    <div className="transaction-details-page">
      <FloatingSidebarButton userProfile={userProfile} />
      {/* Header */}
      <div className="transaction-details-header">
        <button
          className="transaction-details-back"
          onClick={() => navigate(-1)}
          aria-label={t.common?.back || "Back"}
          title={t.common?.back || "Back"}
        >
          <FiArrowLeft />
        </button>
        <h1 className="transaction-details-title">{t.transactionDetails?.title || "Transaction Details"}</h1>
        <button
          className="transaction-details-pdf"
          aria-label={t.transactionDetails?.exportPdf || "Export PDF"}
          title={t.transactionDetails?.exportPdf || "Export PDF"}
          onClick={handleExportPDF}
        >
          <FiFileText />
        </button>
      </div>

      <div className="transaction-details-content" ref={printRef} id="print-area">
        {/* Summary Card */}
        <div className="td-summary-card">
          <div className="td-summary-avatar">
            <img src={userAvatar} alt={userName || 'Profile'} />
          </div>
          <div className="td-summary-username">{userUsername || userName}</div>
          <div className={`td-summary-amount ${isIncoming ? 'positive' : 'negative'}`}>
            {isIncoming ? '+' : '-'} {transactionAmount} WIN
          </div>
          <div className="td-summary-usd">
            ≈ {formatUSD(Math.abs(transaction.amount || 0), winiToUsdRate)} USD
          </div>
        </div>

        {/* Transaction Details Section */}
        <div className="td-section">
          <h2 className="td-section-title">Transaction Details</h2>
          <div className="td-details-card">
            {/* Transaction ID */}
            <div className="td-detail-row">
              <div className="td-detail-icon">
                <FiHash />
              </div>
              <div className="td-detail-content">
                <div className="td-detail-label">Transaction ID</div>
                <div className="td-detail-value">{transaction.transaction_id || transaction.id}</div>
              </div>
            </div>

            {/* Source */}
            <div className="td-detail-row">
              <div className="td-detail-icon">
                <FiFolder />
              </div>
              <div className="td-detail-content">
                <div className="td-detail-label">Source</div>
                <div className="td-detail-value">{transaction.source || 'wallet'}</div>
              </div>
            </div>

            {/* Flow */}
            <div className="td-detail-row">
              <div className="td-detail-icon">
                <FiRepeat />
              </div>
              <div className="td-detail-content">
                <div className="td-detail-label">Flow</div>
                <div className={`td-detail-value td-flow-badge ${isIncoming ? 'in' : 'out'}`}>
                  {isIncoming ? 'in' : 'out'}
                </div>
              </div>
            </div>

            {/* Field */}
            <div className="td-detail-row">
              <div className="td-detail-icon">
                <FiFile />
              </div>
              <div className="td-detail-content">
                <div className="td-detail-label">Field</div>
                <div className="td-detail-value">{transaction.field || 'winicoins'}</div>
              </div>
            </div>

            {/* Description */}
            {transaction.description && (
              <div className="td-detail-row">
                <div className="td-detail-icon">
                  <FiFileTextIcon />
                </div>
                <div className="td-detail-content">
                  <div className="td-detail-label">Description</div>
                  <div className="td-detail-value">{transaction.description}</div>
                </div>
              </div>
            )}

            {/* Date & Time */}
            <div className="td-detail-row">
              <div className="td-detail-icon">
                <FiClock />
              </div>
              <div className="td-detail-content">
                <div className="td-detail-label">Date & Time</div>
                <div className="td-detail-value">{formatDateTime(transaction.time)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Balance Information Section */}
        <div className="td-section">
          <h2 className="td-section-title">Balance Information</h2>
          
          {/* Previous Balance */}
          <div className="td-balance-card">
            <div className="td-balance-icon">
              <FiArrowLeftIcon />
            </div>
            <div className="td-balance-content">
              <div className="td-balance-label">Previous Balance</div>
              <div className="td-balance-main">{oldBalance} WIN</div>
              <div className="td-balance-usd">≈ {formatUSD(transaction.oldBalance || 0, winiToUsdRate)} USD</div>
            </div>
          </div>

          {/* Transaction Amount */}
          <div className="td-balance-card">
            <div className="td-balance-icon">
              <FiRepeat />
            </div>
            <div className="td-balance-content">
              <div className="td-balance-label">Transaction Amount</div>
              <div className="td-balance-main">{transactionAmount} WIN</div>
              <div className="td-balance-usd">≈ {formatUSD(Math.abs(transaction.amount || 0), winiToUsdRate)} USD</div>
            </div>
          </div>

          {/* Fee (if exists) */}
          {transaction.fee_amount_in_coins && transaction.fee_amount_in_coins > 0 && (
            <div className="td-balance-card">
              <div className="td-balance-icon">
                <FiDollarSign />
              </div>
              <div className="td-balance-content">
                <div className="td-balance-label">Fee {feePercentage && `(${feePercentage})`}</div>
                <div className="td-balance-main">{feeAmount} WIN</div>
                <div className="td-balance-usd">≈ {formatUSD(transaction.fee_amount_in_coins || 0, winiToUsdRate)} USD</div>
              </div>
            </div>
          )}

          {/* New Balance */}
          <div className="td-balance-card">
            <div className="td-balance-icon">
              <FiArrowRight />
            </div>
            <div className="td-balance-content">
              <div className="td-balance-label">New Balance</div>
              <div className="td-balance-main">{newBalance} WIN</div>
              <div className="td-balance-usd">≈ {formatUSD(transaction.newBalance || 0, winiToUsdRate)} USD</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
